import React, { useEffect, useState } from "react";
import "./App.css";
import { Toaster } from "sonner";
import OfflineBanner from "./components/OfflineBanner/OfflineBanner";

//Global accessable components
// State
import { Provider as MobxProvider } from "mobx-react";

//stores
import EventStore from "./mobx/EventStore";
import NewsStore from "./mobx/NewsStore";
import StoryStore from "./mobx/StoryStore";
import AppDialogStore from "./mobx/AppDialogStore";
import ShopStore from "./mobx/ShopStore";
import UserStore from "./mobx/UserStore";
import CodeInputStore from "./mobx/CodeInputStore";
import CustomTrikotStore from "./mobx/CustomTrikotStore";
import MohrenRouter from "./router/MohrenRouter";
import QuestionStore from "./mobx/QuestionStore";

import Maintenance from "./components/Maintenance/Maintenance";

import AgbModal from "./components/AgbModal/AgbModal";
import AppDialog from "./components/AppDialog/AppDialog";
import VersionChecker from "./components/VersionChecker/VersionChecker";
import VersionDebugHelper from "./components/VersionChecker/VersionDebugHelper";

const eventStore = new EventStore();
const newsStore = new NewsStore();
const storyStore = new StoryStore();
const appDialogStore = new AppDialogStore();
const shopStore = new ShopStore();
const customTrikotStore = new CustomTrikotStore();
const userStore = new UserStore();
const codeInputStore = new CodeInputStore();
const questionStore = new QuestionStore();

questionStore.addUserStore(userStore);
storyStore.addUserStore(userStore);
appDialogStore.addUserStore(userStore);
codeInputStore.addUserStore(userStore);

const App = () => {
  const [hasStripeCookie, setHasStripeCookie] = useState(false);
  const [showAgbModal, setShowAgbModal] = useState(false);

  const handleCookieChange = (event) => {
    if (!hasStripeCookie) {
      setHasStripeCookie(true);
    }
  };

  const handleAgbAccept = async () => {
    await userStore.setAgbChecked(true);
    setShowAgbModal(false);
  };

  // Load configurable app dialogs once the user is available after login.
  // Key on the user id so the effect doesn't refire when other user fields
  // change (e.g. seenDialogs after a dismiss).
  useEffect(() => {
    if (userStore.currentUser) {
      appDialogStore.loadAppDialogs();
    }
  }, [userStore.currentUser?.id]);

  // Check if user needs to see AGB modal
  useEffect(() => {
    const checkAgbStatus = () => {
      if (userStore.currentUser && !userStore.currentUser.agbChecked) {
        setShowAgbModal(true);
      }
    };

    // Check immediately if user is already loaded
    if (userStore.currentUser) {
      checkAgbStatus();
    }

    // Also check when user data changes
    const interval = setInterval(() => {
      if (
        userStore.currentUser &&
        !userStore.currentUser.agbChecked &&
        !showAgbModal
      ) {
        setShowAgbModal(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [userStore.currentUser, showAgbModal]);

  useEffect(() => {
    // Skip Cookiebot gate in development — consent banner can't load on localhost/10.0.2.2
    if (import.meta.env.DEV) {
      setHasStripeCookie(true);
      return;
    }

    const cookiebot = (window as any).Cookiebot;
    const stamp = cookiebot?.consent?.stamp;
    if (stamp && stamp !== 0 && stamp !== "0" && stamp !== "") {
      setHasStripeCookie(true);
    } else {
      window.addEventListener("CookiebotOnAccept", handleCookieChange);
      window.addEventListener("CookiebotOnDecline", handleCookieChange);
    }

    return () => {
      window.removeEventListener("CookiebotOnAccept", handleCookieChange);
      window.removeEventListener("CookiebotOnDecline", handleCookieChange);
    };
  }, []);

  const renderRouting = () => {
    if (hasStripeCookie) {
      return (
        <MobxProvider
          eventStore={eventStore}
          newsStore={newsStore}
          customTrikotStore={customTrikotStore}
          codeInputStore={codeInputStore}
          storyStore={storyStore}
          appDialogStore={appDialogStore}
          shopStore={shopStore}
          userStore={userStore}
          questionStore={questionStore}
        >
          <MohrenRouter />
          <AppDialog />
        </MobxProvider>
      );
    }

    return <></>;
  };

  return (
    <>
      <OfflineBanner />
      <Toaster
        richColors
        closeButton
        position="top-center"
        duration={6000}
      />
      <VersionChecker />
      {import.meta.env.VITE_MAINTENANCE === "true" ? (
        <Maintenance />
      ) : (
        <div className="app-main-container">{renderRouting()}</div>
      )}

      <AgbModal visible={showAgbModal} onAccept={handleAgbAccept} />
    </>
  );
};

export default App;
